'use client'

import { useEffect, useRef, useState } from 'react'
import { useLocale, useTranslations } from 'next-intl'
import { Loader2, Star } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useConfig } from '@/context/ConfigProvider'
import { useChatStore } from '@/store/chat.store'
import { usePersistStore } from '@/store/persist.store'
import { getOperatorAvatarLabel } from '@/lib/live-support'
import { themeDisplayName, type OperatorRatingDraft, type SubmitRatingPayload } from '@/lib/operator-review'

export default function LiveSupportReviewCard() {
  const t = useTranslations('review')
  const locale = useLocale()
  const { apiUrl } = useConfig()

  const pendingReview = useChatStore(state => state.pendingReview)
  const setPendingReview = useChatStore(state => state.setPendingReview)
  const addReviewedSession = usePersistStore(state => state.addReviewedSession)

  const sessionId = pendingReview?.session_id ?? null

  const operators = pendingReview?.operators ?? []
  const themes = pendingReview?.themes ?? []
  const [drafts, setDrafts] = useState<Record<string, OperatorRatingDraft>>(() =>
    Object.fromEntries(operators.map(op => [op.id, { themeStars: {} }]))
  )
  const [hoverStars, setHoverStars] = useState<Record<string, number>>({})
  const [feedback, setFeedback] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Drives the fade in/out of the full-widget overlay. Starts hidden, fades in
  // once mounted, and fades out before the pending review is actually cleared.
  const [visible, setVisible] = useState(false)
  const fadeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const FADE_MS = 200

  // Fade the overlay out, then clear the pending review once the transition ends.
  const dismiss = () => {
    if (sessionId) addReviewedSession(sessionId)
    setVisible(false)
    if (fadeTimer.current) clearTimeout(fadeTimer.current)
    fadeTimer.current = setTimeout(() => setPendingReview(null), FADE_MS)
  }

  // Fade the overlay in on the next frame once we have a session to review.
  useEffect(() => {
    if (!sessionId) return
    const id = requestAnimationFrame(() => setVisible(true))

    return () => cancelAnimationFrame(id)
  }, [sessionId])

  useEffect(() => {
    return () => {
      if (fadeTimer.current) clearTimeout(fadeTimer.current)
    }
  }, [])

  if (!sessionId) return null

  if (operators.length === 0) return null

  const setThemeStars = (opId: string, theme: string, stars: number) =>
    setDrafts(prev => ({
      ...prev,
      [opId]: { themeStars: { ...prev[opId]?.themeStars, [theme]: stars } }
    }))

  const requiredThemes = themes.filter(theme => theme.required)
  const isRated = (opId: string, themeName: string) => (drafts[opId]?.themeStars[themeName] ?? 0) >= 1

  // Validation rule:
  //  - If the site has any required themes → every operator must have all required themes rated.
  //  - Otherwise → at least one theme must be rated (for any operator).
  // Feedback alone never satisfies submit (no feedback-only bypass).
  const canSubmit =
    requiredThemes.length > 0
      ? operators.every(op => requiredThemes.every(theme => isRated(op.id, theme.name)))
      : operators.some(op => Object.values(drafts[op.id]?.themeStars ?? {}).some(s => s >= 1))

  const submit = async () => {
    if (!canSubmit || submitting) return
    setSubmitting(true)
    setError(null)

    const ratings: SubmitRatingPayload[] = operators.map(op => {
      const themeStars = drafts[op.id]?.themeStars ?? {}
      const themeRatings = Object.entries(themeStars)
        .filter(([, stars]) => stars >= 1 && stars <= 5)
        .map(([theme, stars]) => ({ theme, stars }))

      return { operatorId: op.id, themeRatings }
    })

    try {
      const res = await fetch(`${apiUrl}/api/live-support/review`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId, ratings, feedback })
      })

      if (res.ok || res.status === 409) {
        dismiss()

        return
      }

      setError(t('error'))
    } catch {
      setError(t('error'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <ReviewOverlay visible={visible}>
      <div className='flex size-full flex-col'>
        <div className='flex flex-1 flex-col justify-center overflow-y-auto p-4'>
          {operators.map((op, idx) => {
            const draft = drafts[op.id]

            return (
              <div key={op.id} className={cn(idx > 0 && 'mt-5 border-t pt-5')}>
                <div className='mb-3 flex items-center gap-3'>
                  <div className='flex size-9 items-center justify-center rounded-full bg-orange-500/15 text-sm font-semibold text-orange-600 dark:text-orange-400'>
                    {getOperatorAvatarLabel(op.name)}
                  </div>
                  <div className='flex min-w-0 flex-col'>
                    <span className='truncate text-sm font-semibold'>{op.name}</span>
                    <span className='text-muted-foreground text-xs'>{t('rateOperator')}</span>
                  </div>
                </div>

                <div className='mb-3 space-y-2'>
                  {themes.map(theme => {
                    const current = draft?.themeStars[theme.name] || 0
                    const hoverKey = `${op.id}:${theme.name}`
                    const activeStars = hoverStars[hoverKey] || current
                    const label = themeDisplayName(theme, locale)

                    return (
                      <div key={theme.name} className='flex items-center justify-between gap-3'>
                        <span className='text-xs'>
                          {label}
                          {theme.required && <span className='ml-0.5 text-red-500'>*</span>}
                        </span>
                        <div
                          className='flex gap-0.5'
                          onMouseLeave={() => setHoverStars(prev => ({ ...prev, [hoverKey]: 0 }))}
                        >
                          {[1, 2, 3, 4, 5].map(n => (
                            <button
                              key={n}
                              type='button'
                              aria-label={`${label} ${n}`}
                              onClick={() => setThemeStars(op.id, theme.name, n)}
                              onMouseEnter={() => setHoverStars(prev => ({ ...prev, [hoverKey]: n }))}
                              className='cursor-pointer transition-transform hover:scale-110'
                            >
                              <Star
                                className={cn(
                                  'size-5',
                                  n <= activeStars ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground/40'
                                )}
                              />
                            </button>
                          ))}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })}

          <textarea
            value={feedback}
            onChange={e => setFeedback(e.target.value)}
            maxLength={1000}
            placeholder={t('feedbackPlaceholder')}
            className='border-border bg-background mt-4 h-14 w-full resize-none rounded-lg border p-2.5 text-xs outline-none'
          />

          {error && <p className='mt-2 text-xs text-red-600'>{error}</p>}
        </div>

        <div className='flex shrink-0 items-center justify-end gap-3 border-t p-4'>
          <button type='button' onClick={dismiss} className='text-muted-foreground cursor-pointer text-xs'>
            {t('skip')}
          </button>
          <button
            type='button'
            onClick={submit}
            disabled={!canSubmit || submitting}
            className='cursor-pointer rounded-lg bg-orange-500 px-4 py-2 text-xs font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50'
          >
            {submitting ? <Loader2 className='size-4 animate-spin' /> : t('submit')}
          </button>
        </div>
      </div>
    </ReviewOverlay>
  )
}

/**
 * Full-widget overlay shell: fills the entire chat widget edge-to-edge with a
 * solid surface and fades in/out via opacity.
 */
function ReviewOverlay({ visible, children }: { visible: boolean; children: React.ReactNode }) {
  return (
    <div
      className={cn(
        'bg-card absolute inset-0 z-[100] transition-opacity duration-200 ease-out',
        visible ? 'opacity-100' : 'opacity-0'
      )}
    >
      {children}
    </div>
  )
}
